/**
 * The build id of the web app and the check for a newer build (the FYM Web pattern,
 * `fym-web/src/system/version/versionCheck.ts`). `craco.config.js` writes the id into the bundle
 * (`process.env.M24_BUILD_ID`) and writes `{ version, buildId }` into `build/version.json`. nginx serves that file no-store at
 * `/app/version.json`.
 */

/** The address of `version.json` next to `index.html`, under the public path (`/app`). */
export function versionUrl(publicUrl: string = process.env.PUBLIC_URL ?? ''): string {
  return `${publicUrl.replace(/\/+$/, '')}/version.json`;
}

/** The build id of this page. A build with no id (a test) is `dev`. */
export function currentBuildId(): string {
  const id = process.env.M24_BUILD_ID;
  return typeof id === 'string' && id !== '' ? id : 'dev';
}

/**
 * What `version.json` holds: `{ version, buildId }`. The version is null in a file of a build
 * before the version field (only `{ buildId }`).
 */
export interface VersionInfo {
  buildId: string;
  version: string | null;
}

/**
 * Reads `version.json` from the server. Null when the file is missing or unreadable (the dev server
 * answers its index page, a proxy page, no network) or has no build id. Never from a cache.
 */
export async function fetchVersionInfo(
  fetchImpl: typeof fetch = fetch,
  url: string = versionUrl()
): Promise<VersionInfo | null> {
  try {
    const response = await fetchImpl(url, { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) {
      return null;
    }
    const body: unknown = await response.json();
    if (typeof body !== 'object' || body === null) {
      return null;
    }
    const { buildId, version } = body as { buildId?: unknown; version?: unknown };
    if (typeof buildId !== 'string' || buildId === '') {
      return null;
    }
    return { buildId, version: typeof version === 'string' && version !== '' ? version : null };
  } catch {
    return null;
  }
}

/** Reads the build id that the server holds now. Null for an unknown answer (see `fetchVersionInfo`). */
export async function fetchBuildId(
  fetchImpl: typeof fetch = fetch,
  url: string = versionUrl()
): Promise<string | null> {
  return (await fetchVersionInfo(fetchImpl, url))?.buildId ?? null;
}

/** True when the server names another build than this page. An unknown answer is no update. */
export function isNewBuild(serverBuildId: string | null, ownBuildId: string): boolean {
  return serverBuildId !== null && serverBuildId !== ownBuildId;
}
